import React from 'react';
import { hamtaByggstart, arDatum } from '../../utils/projectDates';
import { arOriginal } from '../../utils/offertSpar';

/**
 * ProjectTimeline - Kompakt tidslinje-komponent för kundorienterade projektsidor
 *
 * Visar en kompakt horisontell tidslinje med status (Klart, Nästa steg, Kommande)
 */
const ProjectTimeline = ({ timelineData, project, procurement, offertFollowupRequests = [] }) => {
  if (!timelineData) return null;

  const { latest, isApproved, inProcurement, homeVisitAt, steps, hasSentOffers, offerter } = timelineData;

  const offerterArray = Array.isArray(offerter) ? offerter : [];

  const followupAt = latest?.estimatFollowup?.meetingAt ? new Date(latest.estimatFollowup.meetingAt) : null;

  const preStudyAt = (() => {
    if (latest?.preStudy?.meetingAt) {
      const d = new Date(latest.preStudy.meetingAt);
      if (!isNaN(d)) return d;
    }
    try {
      if (latest && latest.leadId && typeof latest.leadId === 'object') {
        const cand = latest.leadId.preStudyMeetingTime || latest.leadId.meetingTime;
        if (cand) {
          const d2 = new Date(cand);
          if (!isNaN(d2)) return d2;
        }
      }
    } catch (_) {}
    return null;
  })();

  const nowTs = Date.now();

  const stepStates = steps.map(() => 'todo');

  const firstOffer = hasSentOffers ? offerterArray
    .filter(offer => ['sent', 'viewed', 'accepted', 'declined', 'commented'].includes(offer.status))
    .sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt))[0] : null;
  const offerSentAt = firstOffer?.sentAt ? new Date(firstOffer.sentAt) : null;

  // Originalspåret. Tidslinjen visar AFFÄRENS framsteg — ett godkänt tillägg
  // under produktion är inte den punkt där offerten accepterades, och skulle
  // annars flytta steget till ett datum månader senare.
  const acceptedOffer = offerterArray.find(offer => arOriginal(offer) && offer.status === 'accepted');
  const acceptedOfferDate = acceptedOffer?.respondedAt ? new Date(acceptedOffer.respondedAt) : null;

  if (inProcurement) {
    if (preStudyAt) {
      stepStates[0] = preStudyAt.getTime() > nowTs ? 'next' : 'done';
    }
    stepStates[1] = 'done';
    if (followupAt) {
      stepStates[2] = followupAt.getTime() > nowTs ? 'next' : 'done';
    }
    if (homeVisitAt || isApproved) {
      if (homeVisitAt) {
        stepStates[3] = homeVisitAt.getTime() > nowTs ? 'next' : 'done';
      } else {
        stepStates[3] = 'next';
      }
    }

    if (hasSentOffers) {
      stepStates[4] = 'done';

      const followupRequestsForTimeline = procurement?.offertFollowupRequests || offertFollowupRequests || [];
      const latestFollowupRequest = offerterArray.length > 0 && followupRequestsForTimeline.length > 0 ? followupRequestsForTimeline[0] : null;
      const hasBookedOffertFollowup = latestFollowupRequest?.status === 'booked' && latestFollowupRequest?.consultantResponse?.acceptedSlot;

      if (hasBookedOffertFollowup) {
        const meetingTime = latestFollowupRequest?.consultantResponse?.acceptedSlot;
        let isMeetingCompleted = false;

        if (meetingTime) {
          const dateMatch = meetingTime.match(/(\d{2})\/(\d{2})/);
          const timeMatch = meetingTime.match(/(\d{2}:\d{2})/);

          if (dateMatch && timeMatch) {
            const day = parseInt(dateMatch[1]);
            const month = parseInt(dateMatch[2]) - 1;
            const year = new Date().getFullYear();
            const [hours, minutes] = timeMatch[1].split(':').map(Number);

            const meetingDate = new Date(year, month, day, hours, minutes);
            const now = new Date();

            const meetingEndTime = new Date(meetingDate.getTime() + 60 * 60 * 1000);
            isMeetingCompleted = now > meetingEndTime;
          }
        }

        if (isMeetingCompleted) {
          stepStates[5] = 'done';
          stepStates[6] = 'next';
        } else {
          stepStates[5] = 'next';
        }
      } else {
        stepStates[5] = 'next';
      }

      if (acceptedOffer) {
        stepStates[6] = 'done';
      }
    } else if (homeVisitAt && homeVisitAt.getTime() <= nowTs) {
      stepStates[4] = 'next';
    }
  } else {
    const projectStatus = project?.status;
    const projectStartDate = project?.startDate ? new Date(project.startDate) : null;
    const isStarted = projectStatus === 'Pågående' || projectStatus === 'Avslutad';
    const agreementSignedAt = project?.agreementSignedAt ? new Date(project.agreementSignedAt) : null;
    const { projektgenomgang, hvfExists } = timelineData;

    stepStates[0] = isStarted ? 'done' : 'next';

    if (projektgenomgang) {
      const meetingDate = new Date(projektgenomgang.scheduledAt);
      stepStates[1] = (meetingDate < nowTs) ? 'done' : 'next';
    }

    if (agreementSignedAt) {
      stepStates[2] = 'done';
    } else if (hvfExists) {
      stepStates[2] = 'next';
    }

    if (isStarted) {
      stepStates[3] = 'done';
    } else if (projectStartDate && !isNaN(projectStartDate)) {
      stepStates[3] = 'next';
    }
  }

  const stateFor = (idx) => stepStates[idx] || 'todo';

  const stepDescriptions = {
    'Förstudie': 'Inledande digitalt möte för att förstå behov och planera arbetet.',
    'Estimat': 'En första kostnadsuppskattning att granska och ge feedback på.',
    'Uppföljning Estimat': 'Digital genomgång via Teams för att svara på frågor och planera nästa steg.',
    'Hembesök': 'Konsult besöker på plats för att verifiera förutsättningar och detaljer.',
    'Offert': 'Offert tas fram och skickas ut baserat på överenskommen omfattning.',
    'Offertuppföljning': 'Vi går igenom offerten tillsammans och besvarar eventuella frågor.',
    'Boka byggare': 'Val av entreprenör och bokning/kontraktsskrivning.',
    'Projektering': 'Planering, ritningar och detaljering inför byggstart.',
    'Avtal': 'Avtal signeras digitalt via Scrive.',
    'Projektgenomgång': 'Startmöte där plan, tider och ansvar gås igenom.',
    'Byggstart': 'Byggstart enligt överenskommen plan.'
  };

  // Helper to get date string for a step
  const getStepDate = (label, idx) => {
    const st = stateFor(idx);
    if (st === 'todo') return null;

    if (label === 'Förstudie' && preStudyAt) {
      return preStudyAt.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', timeZone: 'UTC' });
    }
    if (label === 'Estimat' && latest?.sentAt) {
      return new Date(latest.sentAt).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
    }
    if (label === 'Uppföljning Estimat' && followupAt) {
      return followupAt.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', timeZone: 'UTC' });
    }
    if (label === 'Hembesök' && homeVisitAt) {
      return homeVisitAt.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', timeZone: 'UTC' });
    }
    if (label === 'Offert' && offerSentAt && st === 'done') {
      return offerSentAt.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
    }
    if (label === 'Boka byggare' && acceptedOfferDate && st === 'done') {
      return acceptedOfferDate.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
    }
    if (label === 'Projektgenomgång' && timelineData.projektgenomgang?.scheduledAt) {
      return new Date(timelineData.projektgenomgang.scheduledAt).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', timeZone: 'UTC' });
    }
    if (label === 'Avtal' && project?.agreementSignedAt && st === 'done') {
      return new Date(project.agreementSignedAt).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
    }
    if (label === 'Byggstart') {
      // Tidplanen gäller före projektets egna fält, som ofta är fritext
      const byggstart = hamtaByggstart(project);
      if (!arDatum(byggstart)) return null;
      const d = new Date(byggstart);
      return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', timeZone: 'Europe/Stockholm' });
    }
    if (label === 'Offertuppföljning') {
      const followupRequestsForDate = procurement?.offertFollowupRequests || offertFollowupRequests || [];
      const latestFollowupRequest = followupRequestsForDate.length > 0 ? followupRequestsForDate[0] : null;
      const hasBookedMeeting = latestFollowupRequest?.status === 'booked' && latestFollowupRequest?.consultantResponse?.acceptedSlot;
      if (hasBookedMeeting) {
        const meetingTime = latestFollowupRequest.consultantResponse.acceptedSlot;
        const dateMatch = meetingTime.match(/(\d{2})\/(\d{2})/);
        if (dateMatch) {
          return `${dateMatch[1]}/${dateMatch[2]}`;
        }
        return meetingTime;
      }
    }
    return null;
  };

  return (
    <div className="mb-3">
      {/* Legend */}
      <div className="flex items-center gap-3 mb-1.5 text-[10px] text-gray-500 justify-center">
        <span className="inline-flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> Klart
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span> Nästa steg
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-300"></span> Kommande
        </span>
      </div>

      {/* Steps */}
      <div className="flex items-center gap-0.5 overflow-x-auto scrollbar-hide py-1 justify-center">
        {steps.map((label, idx) => {
          const st = stateFor(idx);
          const chipClass = st === 'done'
            ? 'bg-green-50 border-green-300 text-green-700'
            : st === 'next'
              ? 'bg-blue-50 border-blue-300 text-blue-700'
              : 'bg-gray-50 border-gray-200 text-gray-400';
          const dotClass = st === 'done' ? 'bg-green-500' : st === 'next' ? 'bg-blue-500' : 'bg-gray-300';
          const connectorClass = st === 'done' ? 'bg-green-300' : 'bg-gray-200';
          const dateStr = getStepDate(label, idx);

          return (
            <React.Fragment key={label}>
              <div className={`relative group flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full border text-[10px] sm:text-xs whitespace-nowrap flex-shrink-0 no-underline cursor-default ${chipClass}`}>
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`}></span>
                <span className="leading-tight">
                  {label}
                  {dateStr && (
                    <span className="hidden sm:inline text-[9px] text-gray-500 ml-1">
                      {dateStr}
                    </span>
                  )}
                </span>
                {/* Tooltip */}
                <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-10 whitespace-nowrap rounded-md bg-gray-900 text-white text-[10px] px-2.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                  {stepDescriptions[label] || ''}
                  {dateStr && <span className="block text-gray-300">{dateStr}</span>}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`w-2 sm:w-3 h-[1.5px] flex-shrink-0 ${connectorClass}`}></div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default ProjectTimeline;
